// React
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// Context
import { AppContext } from '../../../model/context/app/app';

// Libs
import { Icon, Loader, Message, Segment } from 'semantic-ui-react';
import _ from 'lodash';

// Components
import LeadDetailsToolbarComponent from './LeadDetailsToolbar.component';
import LeadDetailsBoxesComponent from '../../../components/lead/detail/LeadDetailsBoxes.component';
import LeadCloseConfirmationModalComponent from '../../../components/lead/detail/LeadCloseConfirmationModal';
import LeadWorkflowModalComponent from '../../../components/lead/detail/LeadWorkflowModal';
import LeadErrorMessageComponent from '../../../components/lead/detail/LeadErrorMessage.component';

// Services
import ACL_importLeadDetails from '../../../services/ACL/ACL_importLeadDetails';
import fetchLeadItem from '../../../services/fetch/fetchLeadItem';
import fetchWorkflowTask from '../../../services/fetch/fetchWorkflowTask';
import fetchPendingActivites from '../../../services/fetch/fetchPendingActivites';

// Types
import { PendingActivities } from '../../../types/Errors';
import { WorkflowStatesContext } from '../../../model/context/app/workflowStates';
import { useTranslation } from 'react-i18next';

/**
 * Detail view of a special lead.
 *
 * @returns React.ReactElement
 */
export default function LeadDetailsComponent(): React.ReactElement {
    const context = useContext(AppContext);
    const workflowStates = useContext(WorkflowStatesContext);
    const { leadId } = useParams();
    const { t } = useTranslation();

    const [token, setToken] = useState('');
    const [leadDetails, setLeadDetails] = useState<any>();
    const [loading, setLoading] = useState(true);
    const [tasks, setTasks] = useState<any>();
    const [canClose, setCanClose] = useState<boolean>(false);
    const [pendingActivities, setPendingActivities] = useState<Array<PendingActivities>>();

    const [openTaskModal, setOpenTaskModal] = useState(false);
    const [openConfirmation, setOpenConfirmation] = useState(false);

    async function onFinishTask(refresh?: boolean) {
        if (refresh) {
            fetchDataWithDelay();
        }
        setOpenTaskModal(false);
    }

    async function getWorkflowTasks(task: string) {
        const taskResponse = await fetchWorkflowTask(leadId || '', token);
        if (taskResponse.status === 204) {
            setTasks(undefined);
        } else if (taskResponse.status === 200 && taskResponse.data) {
            const task = await taskResponse.data;
            setTasks(task);
        } else {
            console.error('error on reading pending activities', taskResponse.error);
        }
    }

    async function getPendingActivities() {
        const activitiesResponse = await fetchPendingActivites(leadId || '', token);
        if (activitiesResponse.status === 200) {
            const activities = await activitiesResponse.data;
            setPendingActivities(activities);
        } else {
            console.error('error on reading pending activities', activitiesResponse.error);
        }
    }

    async function getLeadDetails() {
        const leadItemsResponse = await fetchLeadItem(leadId, token);
        if (leadItemsResponse.status === 200 && leadItemsResponse.data) {
            const leadData = await leadItemsResponse.data;
            setLeadDetails(ACL_importLeadDetails(leadData));
            if (
                workflowStates.states.workflowTodos.length > 0 &&
                workflowStates.states.workflowOpen.length > 0 &&
                leadData.status.length > 0
            ) {
                setCanClose(workflowStates.states.workflowOpen.includes(leadData.status));
                if (workflowStates.states.workflowTodos.includes(leadData.status)) {
                    await getWorkflowTasks(leadData.status);
                } else {
                    setTasks(undefined);
                }
                if (workflowStates.states.workflowOpen.includes(leadData.status)) {
                    await getPendingActivities();
                }
            }
        } else {
            console.log('error on loading lead detail data', leadItemsResponse);
        }
    }

    function fetchDataWithoutDelay() {
        setLoading(true);
        getLeadDetails().finally(() => setLoading(false));
    }

    // this is necessary since the backend is triggering an async process, which might take some time and we have no
    // reliable flag to ask if the async process is finished.
    function fetchDataWithDelay() {
        setLoading(true);
        _.delay(() => {
            getLeadDetails().finally(() => setLoading(false));
        }, 1000);
    }

    useEffect(() => {
        if (token) {
            fetchDataWithoutDelay();
        }
    }, [token]);

    useEffect(() => {
        setToken(context.token);
    }, [context]);

    return (
        <>
            <Segment basic>
                <LeadDetailsToolbarComponent
                    canClose={canClose}
                    task={tasks?.task}
                    onModalOpen={setOpenTaskModal}
                    onConfirmationOpen={setOpenConfirmation}
                    onRefresh={fetchDataWithoutDelay}
                />
            </Segment>

            <LeadErrorMessageComponent pendingActivities={pendingActivities} />

            {leadDetails && leadDetails?.rateLimitExceeded && (
                <Segment basic>
                    <Message warning>
                        <Icon name="warning sign" /> {t(`lead.list.multipleRequests`)}
                    </Message>
                </Segment>
            )}

            <Segment basic>
                <LeadDetailsBoxesComponent detailsData={leadDetails} />
                <div style={{ height: '120px' }}>
                    <Loader active={loading} />
                </div>
                {tasks && (
                    <LeadWorkflowModalComponent
                        leadId={leadId}
                        task={tasks}
                        active={openTaskModal}
                        onClose={onFinishTask}
                        token={context.token}
                    />
                )}
                {openConfirmation && (
                    <LeadCloseConfirmationModalComponent
                        leadId={leadId}
                        active={openConfirmation}
                        onClose={async () => {
                            setOpenConfirmation(false);
                            fetchDataWithDelay();
                        }}
                    />
                )}
            </Segment>

            <Segment basic>
                <div></div>
            </Segment>
        </>
    );
}
